/*
 * ecom-search-fs.js  (Search fullscreen v1, Wes 2026-10-17; spec: design/search_dropdown_2026-10-17/v3_fullscreen)
 *
 * Full-screen search dialog driven by the ONE header input (#ecom-header-search-input). The input is never
 * duplicated: while the dialog is open its form is position:fixed into the dialog's top-bar slot.
 *
 * Algolia usage:
 *   - Nothing at page load. Client created lazily on focus / pointerenter; first request after >= 2 chars.
 *   - Typing: debounced, ONE request per query (hits + all facets in the same call). Cache keyed by query+filters.
 *   - Applying / removing a filter = one new request (no debounce). Facet search boxes filter the facet values
 *     already returned (maxValuesPerFacet 100) on the client: no searchForFacetValues calls.
 *   - Disjunctive feel WITHOUT extra queries: a facet that has selections keeps listing the values it had in
 *     the last reply for the same text where that facet was not filtered.
 *   - Request numbering: every request gets reqSeq; only the reply to the latest request renders (hits, facets,
 *     chips, counts all from that one reply).
 *   - Counts: facet counts are never shown (approximate / variant-level). The total stays unless Algolia flags
 *     both nbHits and facet counts as approximate.
 *   - strict-2026-10-19: every query is strict (removeWordsIfNoResults 'none', optionalWords [], description text not
 *     searched) so no typed word is silently dropped. On 0 hits, ONE follow-up multi-query fetches labelled alternatives
 *     (filters dropped / type word dropped) before the reply resolves, so latest-reply-wins still covers it.
 *   - Crawler guard + ?qa_search=1 QA override exactly as in the dropdown (ecom-search-overlay-lazy.js).
 */
(function () {
  'use strict';

  var MIN_CHARS = 2, DEBOUNCE_MS = 250, HITS = 20, MAX_FACET_VALUES = 100;
  var CONFIG = { appId: 'SKNBBZXEDC', indexName: 'shopify_products' };
  var ALGOLIA_LIB = 'https://cdn.jsdelivr.net/npm/algoliasearch@4.20.0/dist/algoliasearch-lite.umd.js';
  var RECENT_KEY = 'ecomSdRecent', VIEW_KEY = 'ecomSxView', OPEN_KEY = 'ecomSxFacetOpen';
  var POPULAR = ['dowel pins', 'self clinching studs', '1/4-20 x 3/4 fhcs', 'black oxide washers', 'shoulder screws', 'socket set screw', 'phillips flat undercut', '#12-24 screw'];
  var TIPS = [['1/4-20', 'inch thread size'], ['M6', 'metric size'], ['socket cap', 'fastener type'], ['3/8-16 x 1 gr5', 'size + length + grade']];
  var LINKS = { quickOrder: '/pages/quick-order', categories: '/collections' };
  // UI order. Diameter replaces Thread size only when Thread size is not present.
  var FACETS = [
    { key: 'cat', label: 'Category', attr: 'product_type', open: true },
    { key: 'thread', label: 'Thread size', attr: 'meta.custom.thread_size', mono: true, open: true, numeric: true },
    { key: 'dia', label: 'Diameter', attr: 'meta.custom.diameter_nominal_', mono: true, open: true, fallbackFor: 'thread', numeric: true },
    { key: 'len', label: 'Length', attr: 'meta.custom.length_nominal_', mono: true, open: true, numeric: true },
    { key: 'mat', label: 'Material', attr: 'meta.custom.material', open: true },
    { key: 'fin', label: 'Finish', attr: 'meta.custom.finish', open: true },
    { key: 'grade', label: 'Grade', attr: 'meta.custom.grade', open: false },
    { key: 'drive', label: 'Drive', attr: 'meta.custom.drive_type', open: false },
    { key: 'head', label: 'Head', attr: 'meta.custom.head_style', open: false }
  ];
  // strict-2026-10-19: searchable attributes for the query (index default also searches body_html_safe, where
  // "driven with a socket or wrench" made every hex cap match 'socket'). Must be a subset of searchableAttributes.
  var SEARCH_ATTRS = ['title', 'product_type', 'tags', 'sku', 'variant_title', 'vendor', 'handle', 'barcode'];
  var STRICT = { removeWordsIfNoResults: 'none', optionalWords: [], restrictSearchableAttributes: SEARCH_ATTRS };
  var TYPE_WORDS = /^(socket|sockets|shcs|bhcs|fhcs|hex|flat|button|pan|round|truss|oval|cap|caps|head|set|shoulder|carriage|lag|wing|thumb|phillips|slotted|torx|square|flange|countersunk|machine|tapping|wood|lock|nylon|nyloc|jam|acorn|coupling|washer|washers|nut|nuts|bolt|bolts|screw|screws|stud|studs|standoff|standoffs|rivet|rivets|pin|pins|dowel|insert|inserts|anchor|anchors|spacer|spacers)$/i;
  var GENERIC_WORDS = /^(cap|caps|head|screw|screws|bolt|bolts|machine)$/i;
  // strict mode means words are no longer silently dropped, so shop shorthand the index has no synonym for is expanded here
  var ABBR = { fhcs: 'flat head socket cap', shcs: 'socket head cap', bhcs: 'button head socket cap', sss: 'socket set screw', gr2: 'grade 2', gr5: 'grade 5', gr8: 'grade 8' };
  function expandQuery(q) { return String(q).split(/\s+/).map(function (w) { var k = w.toLowerCase(); return ABBR.hasOwnProperty(k) ? ABBR[k] : w; }).join(' '); }
  function strictParams(p) { for (var k in STRICT) if (STRICT.hasOwnProperty(k)) p[k] = STRICT[k]; return p; }
  // the most specific type word(s): drop e.g. 'socket' (keep generic 'cap'/'screw' unless they are the only type words)
  function dropTypeWords(q) {
    var w = String(q).split(/\s+/).filter(Boolean), typ = w.filter(function (x) { return TYPE_WORDS.test(x); });
    if (!typ.length) return null;
    var spec = typ.filter(function (x) { return !GENERIC_WORDS.test(x); }), drop = spec.length ? spec : typ;
    var rest = w.filter(function (x) { return drop.indexOf(x) < 0; });
    if (!rest.length || rest.join(' ').length < MIN_CHARS) return null;
    return { q: rest.join(' '), dropped: drop.join(' ') };
  }
  var FACET_ATTRS = FACETS.map(function (f) { return f.attr; });
  var BY_ATTR = {}; FACETS.forEach(function (f) { BY_ATTR[f.attr] = f; });
  var CHIP_ATTRS = ['meta.custom.finish', 'meta.custom.grade', 'meta.custom.drive_type', 'meta.custom.head_style', 'meta.custom.material'];

  // ---- QA override + crawler guard (same as the dropdown) ----
  var QA = (function () {
    try {
      var m = /[?&]qa_search=([01])/.exec(window.location.search);
      if (m) { if (m[1] === '1') sessionStorage.setItem('ecomQaSearch', '1'); else sessionStorage.removeItem('ecomQaSearch'); }
      return sessionStorage.getItem('ecomQaSearch') === '1';
    } catch (e) { return false; }
  })();
  var IS_BOT = (function () {
    if (QA) return false;
    try { if (navigator.webdriver) return true; return /bot|crawl|spider|slurp|headless|lighthouse/i.test(navigator.userAgent || ''); } catch (e) { return false; }
  })();

  var input = document.getElementById('ecom-header-search-input');
  var root = document.getElementById('ecom-sx');
  if (!input || !root) return;
  var form = input.form;
  var wrapper = document.getElementById('ecom-search-wrapper');
  var clearBtn = document.getElementById('ecom-sd-clear');
  var kbd = wrapper ? wrapper.querySelector('.hv2-kbd') : null;
  var siteHeader = wrapper && wrapper.closest('.ecom-site-header');
  var headerSection = siteHeader && siteHeader.parentElement && /shopify-section/.test(siteHeader.parentElement.className) ? siteHeader.parentElement : null;
  var docEl = document.documentElement;
  var $ = function (id) { return document.getElementById(id); };
  var slot = $('sx-slot'), closeBtn = $('sx-close'), chipsEl = $('sx-chips'), side = $('sx-side'), facetsEl = $('sx-facets'),
      dsel = $('sx-dsel'), barEl = $('sx-bar'), res = $('sx-res'), msgEl = $('sx-msg'), statusEl = $('sx-status'),
      mfoot = $('sx-mfoot'), dim = $('sx-dim'), dClose = $('sx-dclose'), dClear = $('sx-dclear'), dShow = $('sx-dshow'), mainEl = $('sx-main');
  var mqMobile = window.matchMedia('(max-width: 767px)');
  var mqDrawer = window.matchMedia('(max-width: 1023px)');
  var mqMidList = window.matchMedia('(min-width: 1024px) and (max-width: 1279px)');

  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-haspopup', 'dialog');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-controls', 'sx-res');

  // ---- helpers ----
  function esc(s) { return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;'); }
  function fmtInt(n) { try { return Number(n).toLocaleString('en-US'); } catch (e) { return String(n); } }
  function money(n) { if (n == null || isNaN(Number(n))) return ''; try { return '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); } catch (e) { return '$' + Number(n).toFixed(2); } }
  function img(url, size) { if (!url) return ''; return String(url).replace(/(_\d+x\d*|_\d*x\d+)?\.(jpe?g|png|gif|webp)(\?|$)/i, '_' + size + '.$2$3'); }
  function store(k, v) { try { if (v === undefined) return JSON.parse(localStorage.getItem(k)); localStorage.setItem(k, JSON.stringify(v)); } catch (e) { return null; } }
  function sstore(k, v) { try { if (v === undefined) return JSON.parse(sessionStorage.getItem(k)); sessionStorage.setItem(k, JSON.stringify(v)); } catch (e) { return null; } }

  // ---- spec split (SPEC_RE unchanged from ecom-search-overlay-lazy.js v2/v3) ----
  var SPEC_RE = /^((#\d+|\d+(?:\s+\d+\/\d+|\/\d+|\.\d+)?(?:\s?mm)?"?|M\d+(?:\.\d+)?)(?:-(\d+(?:\.\d+)?))?"?(?:\s*[xX\u00d7]\s*(\d+(?:\s+\d+\/\d+|-\d+\/\d+|[\/.]\d+)?(?:\s?(?:mm|"))?))?)(?=[\s,\/]|$)/;
  function parseSpec(title) {
    var t = String(title || ''), m = SPEC_RE.exec(t);
    if (!m) return null;
    var size = m[2], thread = m[3], len = m[4];
    if (!thread && !len && !/["#\/]|mm|^M/i.test(size)) return null;
    var rest = t.slice(m[0].length).replace(/^[\s,\/\-]+/, '');
    if (!rest) return null;
    return { display: m[1].replace(/\s*[xX\u00d7]\s*/, ' \u00d7 '), rest: rest, size: normSize(size), thread: thread || '' };
  }
  function normSize(s) { return String(s || '').toLowerCase().replace(/"/g, '').replace(/\s+/g, ' ').trim(); }
  function querySizes(q) {
    var words = String(q).toLowerCase().replace(/["\u201d]/g, '').split(/\s+/).filter(Boolean), out = [];
    for (var i = 0; i < words.length; i++) {
      var m = /^(#\d+|\d+\/\d+|m\d+(?:\.\d+)?)(?:-(\d+(?:\.\d+)?))?$/.exec(words[i]);
      if (!m) continue;
      var size = m[1], prev = words[i - 1], prev2 = words[i - 2];
      if (prev === 'x' || prev === '\u00d7') continue;
      if (/^\d+\/\d+$/.test(size) && prev && /^\d+$/.test(prev) && !/^(grade|gr|class|cl)$/.test(prev2 || '')) size = prev + ' ' + size;
      out.push({ size: size, thread: m[2] || '' });
    }
    return out;
  }
  function boostExactSize(hits, q) {
    var sizes = querySizes(q); if (!sizes.length) return hits;
    var top = [], rest = [];
    hits.forEach(function (h) { var sp = parseSpec(h.title); (sp && sizes.some(function (s) { return s.size === sp.size && (!s.thread || s.thread === sp.thread); }) ? top : rest).push(h); });
    return top.concat(rest);
  }
  // v2 (QA P3-1): length sort key [group, value]. Group 0 = inch (1 1/4", 3/4", 0.5", 1", #-sizes) by inches,
  // group 1 = metric (4mm, 150 mm) by mm, group 2 = anything unparsable (kept in count order).
  function lenKey(v) {
    var raw = String(v).trim(), s = raw.replace(/[\u201d\u2033]/g, '"'), m;
    if ((m = /^(\d+(?:\.\d+)?)\s*mm$/i.exec(s))) return [1, parseFloat(m[1])];
    if ((m = /^#(\d+)$/.exec(s))) return [0, 0.06 + 0.013 * parseInt(m[1], 10)];
    s = s.replace(/\s*(?:"|in\.?|inch(?:es)?)$/i, '').trim();
    if ((m = /^(\d+)\s+(\d+)\/(\d+)$/.exec(s))) return [0, parseInt(m[1], 10) + m[2] / m[3]];
    if ((m = /^(\d+)\/(\d+)$/.exec(s))) return [0, m[1] / m[2]];
    if ((m = /^(\d+(?:\.\d+)?|\.\d+)$/.exec(s))) return [0, parseFloat(m[1])];
    return [2, 0];
  }
  // size-sort-2026-10-19: verbatim copy of ecom-facets-core.js §1 (Rev5e collection facets: G, inchMarks, frac,
  // magnitude, parseSize, compareSize) so the search drawer orders Thread size / Diameter / Length exactly like the
  // collection chips: #0..#12, inch by decimal (coarse = lower TPI first), metric block (M2, M2.5 .. coarse first), unreadable last.
  // ecom-facets-core.js is not loaded in the header, hence the copy; keep the two in sync.
  var G = { GAUGE: 0, INCH: 1, MM: 2, METRIC: 3, UNKNOWN: 9 };

  // rev 5 / 5b (SORT38): one spelling for the inch mark before parsing. \u201d, \u2033 (double prime) and '' (two
  // apostrophes) are inches; a SINGLE ' stays feet. A trailing in / in. / inch / inches after a number is inches too
  // ("1 in", "3/8 inch", "1in."). Before, 3/8'' read as 4.5" (feet) and 1\u201d / 1 inch sorted as unreadable (last).
  function inchMarks(raw) {
    return String(raw === null || raw === undefined ? '' : raw).trim()
      .replace(/[\u201d\u2033]|''|\u2019\u2019/g, '"')
      .replace(/(\d)\s*(?:in\.?|inch(?:es)?)$/i, '$1"')
      .trim();
  }
  function frac(t) {
    var m = String(t).match(/^(\d+)\/(\d+)$/);
    return m ? parseInt(m[1], 10) / parseInt(m[2], 10) : NaN;
  }
  function magnitude(t) {                       // one inch-ish quantity -> number ("1 1/4", "1-1/4", "3/4", ".25")
    t = inchMarks(t).replace(/"$/, '').trim();
    var mixed = t.match(/^(\d+)[\s-](\d+\/\d+)$/);
    if (mixed) return parseInt(mixed[1], 10) + frac(mixed[2]);
    var f = frac(t);
    if (!isNaN(f)) return f;
    if (!/^\d*\.?\d+$/.test(t)) return NaN;      // stricter than parseFloat: "1/2-13" must not read as 1
    return parseFloat(t);
  }

  function parseSize(raw) {
    var v = inchMarks(raw);
    if (!v) return { group: G.UNKNOWN, a: Infinity, b: 0 };

    // metric designation first (before the "x" split): M6, M6-1.0, M12x1.75, M10 × 1.5. A second number > 6
    // is a length ("M6x20"), not a pitch.
    var met = v.match(/^M\s*(\d+(?:\.\d+)?)(?:\s*(?:-|x|\u00d7)\s*(\d*\.?\d+))?\s*(?:mm)?$/i);
    if (met) {
      var p = met[2] ? parseFloat(met[2]) : 0;
      return { group: G.METRIC, a: parseFloat(met[1]), b: p > 0 && p <= 6 ? p : 0 };
    }

    // spaced range "#10 - #12", ".7 - .9 mm", ".051\" - .080\"": lower bound, carrying a trailing unit over
    var spaced = v.split(/\s+-\s+/);
    if (spaced.length > 1) {
      var lo = spaced[0], hiPart = spaced[spaced.length - 1];
      if (/mm$/i.test(hiPart) && !/mm$/i.test(lo)) lo += ' mm';
      else if (/"$/.test(hiPart) && !/"$/.test(lo) && !/^#/.test(lo)) lo += '"';
      return parseSize(lo);
    }

    // an expression in a single-dimension field ("#8x2 1/2\"", "1/4\"x1\"x0.050"): read the first part
    var xs = v.split(/\s*[x\u00d7]\s*/i);
    if (xs.length > 1 && xs[0].trim()) return parseSize(xs[0]);

    // decimal / inch equivalent in brackets: "#4 (0.115)"
    var brack = v.match(/^([^(]+?)\s*\([^)]*\)\s*$/);
    if (brack) return parseSize(brack[1]);

    // "#5/16" (a # on an inch size; data fault on ~170 products): read as inches
    var hashInch = v.match(/^#\s*(\d+\/\d+|\d+\s+\d+\/\d+)"?$/);
    if (hashInch) return { group: G.INCH, a: magnitude(hashInch[1]), b: 0 };

    // feet, incl. 5/32' and 12'
    if (/'$/.test(v)) {
      var ft = magnitude(v.replace(/'/g, ''));
      return { group: G.INCH, a: isNaN(ft) ? Infinity : ft * 12, b: 0 };
    }

    // millimetres: 10mm, 1.5 mm, .90 mm
    var mm = v.match(/^(\d*\.?\d+)\s*mm$/i);
    if (mm) return { group: G.MM, a: parseFloat(mm[1]), b: 0 };

    // gauge: #10, #10-24, bare 0 / 00 / 000
    var g = v.match(/^#\s*(\d+)(?:\s*-\s*(\d+))?$/) || v.match(/^(0{1,3})$/);
    if (g) return { group: G.GAUGE, a: g[1].length > 1 && /^0+$/.test(g[1]) ? -g[1].length + 1 : parseInt(g[1], 10), b: g[2] ? parseInt(g[2], 10) : 0 };

    // unspaced inch range, both ends quoted: 1/2"-7/8", 9/16"-1-1/16", 1"-2"
    var irange = v.match(/^(.+?")\s*-\s*(.+")$/);
    if (irange) return parseSize(irange[1]);

    // inch with a pitch: 1/4"-20, 1-1/2"-12, 1"-8
    var pitched = v.match(/^(.+?)"\s*-\s*(\d+(?:\.\d+)?)$/);
    if (pitched) {
      var d = magnitude(pitched[1]);
      if (!isNaN(d)) return { group: G.INCH, a: d, b: parseFloat(pitched[2]) };
    }
    // fraction thread without the inch mark: 1/4-20, 1 1/4-7
    var fpitched = v.match(/^(\d+\/\d+|\d+[\s-]\d+\/\d+)\s*-\s*(\d+)$/);
    if (fpitched) return { group: G.INCH, a: magnitude(fpitched[1]), b: parseFloat(fpitched[2]) };

    // bare decimals: ascending pair = range (.188-.250), otherwise size-pitch (4-36)
    var range = v.match(/^(\.?\d+(?:\.\d+)?)\s*-\s*(\.?\d+(?:\.\d+)?)$/);
    if (range) {
      var l = parseFloat(range[1]), h = parseFloat(range[2]);
      if (h > l) return { group: G.INCH, a: l, b: 0 };
      return { group: G.INCH, a: l, b: h };
    }

    var n = magnitude(v);
    if (!isNaN(n)) return { group: G.INCH, a: n, b: 0 };

    // last resort: a readable leading part ("#14L (1/4\")", "#6 NARROW", "1\"-8,7/8\"-9", ".406 | Thickness...")
    var lead = v.match(/^(#\s*\d+)[A-Z]+\b/i) || v.match(/^([^,|]+?)\s*[,|]/) || v.match(/^(#\s*\d+)\s+[A-Z]/i);
    if (lead && lead[1] !== v) return parseSize(lead[1]);
    return { group: G.UNKNOWN, a: Infinity, b: 0 };
  }

  function compareSize(x, y) {
    var A = parseSize(x), B = parseSize(y);
    if (A.group !== B.group) return A.group - B.group;
    if (A.a !== B.a) return A.a < B.a ? -1 : 1;
    if (A.b !== B.b) {
      // inch/gauge: TPI ascending = coarse first. Metric: pitch DESCENDING = coarse first; bare "M10" before pitches.
      if (A.group === G.METRIC) {
        if (!A.b) return -1;
        if (!B.b) return 1;
        return B.b - A.b;
      }
      return A.b - B.b;
    }
    return String(x).localeCompare(String(y), undefined, { numeric: true });
  }

  // fix3: a metric query (M3..M64 token or 'mm') lists mm lengths before inch lengths; otherwise inch first.
  var METRIC_Q = /(?:^|[^a-z0-9])m(?:[3-9]|[1-5]\d|6[0-4])(?:\.\d+)?(?![\d.])|(?:\d\s*|\b)mm\b/i;
  function isMetricQuery(q) { return METRIC_Q.test(String(q || '')); }
  function lenCompare(a, b, metric) {   // size-sort-2026-10-19: Rev5e comparator; metric Length queries list the mm/M block first (fix3)
    if (metric) {
      var A = parseSize(a), B = parseSize(b), ga = A.group === G.MM || A.group === G.METRIC, gb = B.group === G.MM || B.group === G.METRIC;
      if (ga !== gb && A.group !== G.UNKNOWN && B.group !== G.UNKNOWN) return ga ? -1 : 1;
    }
    return compareSize(a, b);
  }

  // ---- state ----
  var isOpen = false, view = null, filters = {}, lastRes = null, lastQ = '', facetMemory = {}, memoryQ = null,
      openFacets = sstore(OPEN_KEY) || {}, expanded = {}, facetSearch = {}, drawerOpen = false, navOrder = [], activeIdx = -1,
      typedSeq = 0, reqSeq = 0, debounceTimer = null, skelTimer = null, suppressFocusOpen = false;
  var STALE = { stale: true };
  FACETS.forEach(function (f) { if (openFacets[f.key] == null) openFacets[f.key] = f.open; });
  function getView() {
    if (mqMobile.matches) return 'rows';
    var v = view || store(VIEW_KEY);
    if (v === 'grid' || v === 'list') return v;
    return mqMidList.matches ? 'list' : 'grid';
  }
  function filterCount() { return Object.keys(filters).reduce(function (n, a) { return n + filters[a].length; }, 0); }
  function filtersKey() { return Object.keys(filters).sort().map(function (a) { return a + '=' + filters[a].slice().sort().join('|'); }).join('&'); }
  function searchUrl(q) {
    var u = '/search?q=' + encodeURIComponent(q);
    Object.keys(filters).forEach(function (a) { filters[a].forEach(function (v, i) { u += '&' + encodeURIComponent('refinementList[' + a + '][' + i + ']') + '=' + encodeURIComponent(v); }); });
    return u;
  }
  var MAX_DISJ = 3; // extra facet-only queries, only when 2+ facets are active
  function activeAttrs() { return Object.keys(filters).filter(function (a) { return filters[a].length; }); }
  function facetFilterParam(except) {
    return Object.keys(filters).filter(function (a) { return filters[a].length && a !== except; }).map(function (a) {
      return filters[a].map(function (v) { return a + ':' + String(v).replace(/^-/, '\\-'); });
    });
  }
  function isFacetsApprox(r) { return !!r && ((r.exhaustive && r.exhaustive.facetsCount === false) || r.exhaustiveFacetsCount === false); }
  function isTotalApprox(r) { return !!r && isFacetsApprox(r) && ((r.exhaustive && r.exhaustive.nbHits === false) || r.exhaustiveNbHits === false); }

  // ---- Algolia (lazy) ----
  var client = null, clientPromise = null, cache = {};
  function getSearchKey() { var c = window.algoliaShopify && window.algoliaShopify.config; return (c && c.search_api_key) || ''; }
  function loadScript(src) { return new Promise(function (ok, ko) { var s = document.createElement('script'); s.src = src; s.async = true; s.onload = ok; s.onerror = ko; document.head.appendChild(s); }); }
  function getClient() {
    if (IS_BOT || !getSearchKey() || !window.Promise) return Promise.resolve(null);
    if (clientPromise) return clientPromise;
    clientPromise = (typeof window.algoliasearch === 'function' ? Promise.resolve() : loadScript(ALGOLIA_LIB))
      .then(function () { client = window.algoliasearch(CONFIG.appId, getSearchKey()); return client; })
      .catch(function (e) { clientPromise = null; if (window.console) console.warn('[ecom-search] Algolia client failed to load', e); return null; });
    return clientPromise;
  }
  function runSearch(q) {
    var key = q.toLowerCase() + '#' + filtersKey();
    if (cache[key]) return Promise.resolve(cache[key]);
    var mySeq = ++reqSeq, ff = facetFilterParam(), aq = expandQuery(q);
    return getClient().then(function (c) {
      if (!c) return null;
      var params = {
        hitsPerPage: HITS, attributesToRetrieve: ['title', 'handle', 'image', 'price', 'variants_min_price', 'product_type'],
        attributesToHighlight: [], attributesToSnippet: [], facets: FACET_ATTRS, maxValuesPerFacet: MAX_FACET_VALUES,
        facetingAfterDistinct: true, analyticsTags: ['header-fullscreen']
      };
      strictParams(params);
      if (ff.length) params.facetFilters = ff;
      var reqs = [{ indexName: CONFIG.indexName, query: aq, params: params }];
      var act = activeAttrs(), disjAttrs = act.length >= 2 ? act.slice(0, MAX_DISJ) : [];
      disjAttrs.forEach(function (a) {
        reqs.push({ indexName: CONFIG.indexName, query: aq, params: {
          hitsPerPage: 0, attributesToRetrieve: [], attributesToHighlight: [], attributesToSnippet: [], facets: [a],
          maxValuesPerFacet: MAX_FACET_VALUES, facetingAfterDistinct: true, facetFilters: facetFilterParam(a),
          analytics: false, clickAnalytics: false, analyticsTags: ['header-fullscreen-disjunctive'], removeWordsIfNoResults: 'none', optionalWords: [], restrictSearchableAttributes: SEARCH_ATTRS } });
      });
      return c.search(reqs).then(function (out) {   // ONE HTTP request (multi-query)
        var r = out && out.results && out.results[0];
        if (r && disjAttrs.length) { r._disj = {}; disjAttrs.forEach(function (a, i) { var x = out.results[i + 1]; if (x && x.facets) r._disj[a] = x.facets[a] || {}; }); }
        if (r && !r.nbHits) return withAlternatives(c, aq, r, ff).then(function () { return r; });
        return r;
      }).then(function (r) {
        if (r) { r._sxKey = key; r._sxQueries = (r._sxQueries || 0) + reqs.length; cache[key] = r; }
        if (mySeq !== reqSeq) return STALE;   // latest request wins (the alternatives belong to their request)
        return r || null;
      });
    });
  }

  // strict-2026-10-19: 0 strict hits -> labelled close alternatives, ONE extra multi-query, never a silent word drop
  function withAlternatives(c, q, r, ff) {
    var alts = [], reqs = [];
    function altParams(extra) {
      var p = { hitsPerPage: 6, attributesToRetrieve: ['title', 'handle', 'image', 'price', 'variants_min_price', 'product_type'],
        attributesToHighlight: [], attributesToSnippet: [], analytics: false, clickAnalytics: false, analyticsTags: ['header-fullscreen-alt'] };
      for (var k in extra) if (extra.hasOwnProperty(k)) p[k] = extra[k];
      return strictParams(p);
    }
    var act = activeChips();
    if (act.length) {                              // same words, without the filters
      alts.push({ kind: 'filters', q: q, relaxed: act });
      reqs.push({ indexName: CONFIG.indexName, query: q, params: altParams({ facets: ['meta.custom.material'], maxValuesPerFacet: 10 }) });
    }
    var d = dropTypeWords(q);
    if (d) {                                       // same filters, without the type word
      alts.push({ kind: 'word', q: d.q, dropped: d.dropped });
      var p = altParams({}); if (ff.length) p.facetFilters = ff;
      reqs.push({ indexName: CONFIG.indexName, query: d.q, params: p });
    }
    if (!reqs.length) return Promise.resolve();
    return c.search(reqs).then(function (out) {
      r._alts = alts.map(function (a, i) { var x = out && out.results && out.results[i]; a.nbHits = x ? x.nbHits : 0; a.hits = x ? x.hits || [] : []; a.facets = x && x.facets; return a; }).filter(function (a) { return a.nbHits; });
      r._sxQueries = reqs.length;
    }).catch(function () { r._alts = []; });
  }

  // ---- recent ----
  function getRecent() { var a = store(RECENT_KEY); return Array.isArray(a) ? a.slice(0, 5) : []; }
  function saveRecent(q) { q = String(q || '').trim(); if (q.length < MIN_CHARS) return; var a = getRecent().filter(function (x) { return x.toLowerCase() !== q.toLowerCase(); }); a.unshift(q); store(RECENT_KEY, a.slice(0, 5)); }

  // ---- render ----
  var ICON_X = '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
  var ICON_S = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></svg>';
  var ICON_UP = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M6 15l6-6 6 6"/></svg>';
  var ICON_DOWN = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';
  var ICON_GRID = '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="3" y="3" width="8" height="8" rx="1"/><rect x="13" y="3" width="8" height="8" rx="1"/><rect x="3" y="13" width="8" height="8" rx="1"/><rect x="13" y="13" width="8" height="8" rx="1"/></svg>';
  var ICON_LIST = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M4 6h16M4 12h16M4 18h16"/></svg>';
  function setStatus(t) { if (statusEl) statusEl.textContent = t; }

  // values to list for a facet: current reply values, plus (when the facet itself is filtered) the values it had
  // before it was filtered for this same text, plus the selected ones. Ordered by count, Length numerically.
  function facetValues(f, r) {
    var cur = (r.facets && r.facets[f.attr]) || {}, sel = filters[f.attr] || [], seen = {}, out = [];
    var disj = r._disj && r._disj[f.attr];
    var src = disj ? null : (sel.length && facetMemory[f.attr] ? facetMemory[f.attr] : null);
    function add(k, c) { if (seen[k]) return; seen[k] = 1; out.push([k, c]); }
    if (disj) cur = disj; // this facet's values with every OTHER filter applied, but not its own
    Object.keys(cur).sort(function (a, b) { return cur[b] - cur[a]; }).forEach(function (k) { add(k, cur[k]); });
    if (src) Object.keys(src).sort(function (a, b) { return src[b] - src[a]; }).forEach(function (k) { add(k, src[k]); });
    sel.forEach(function (k) { add(k, 0); });
    var metric = f.key === 'len' && isMetricQuery(lastQ);
    if (f.numeric) out = out.map(function (x, i) { return [x[0], x[1], i]; }).sort(function (a, b) { return lenCompare(a[0], b[0], metric) || (a[2] - b[2]); });
    return out.map(function (x) { return x[0]; });
  }
  function visibleFacets(r) {
    var threadCount = Object.keys((r.facets && r.facets['meta.custom.thread_size']) || {}).length + (filters['meta.custom.thread_size'] || []).length;
    return FACETS.filter(function (f) {
      if (f.fallbackFor === 'thread' && threadCount > 1) return false;
      var vals = facetValues(f, r);
      return vals.length > 1 || (filters[f.attr] || []).length;
    });
  }
  var fid = 0;
  function renderFacets(r) {
    var html = visibleFacets(r).map(function (f) {
      var vals = facetValues(f, r), sel = filters[f.attr] || [], isOpenF = !!openFacets[f.key], id = 'sx-f-' + f.key;
      var term = (facetSearch[f.key] || '').toLowerCase().trim();
      var list = term ? vals.filter(function (v) { return v.toLowerCase().indexOf(term) >= 0; }) : vals;
      var limit = (expanded[f.key] || term) ? 20 : 5;
      var shown = list.slice(0, limit);
      sel.forEach(function (v) { if (shown.indexOf(v) < 0 && (!term || v.toLowerCase().indexOf(term) >= 0)) shown.unshift(v); });
      var body = '';
      if (vals.length > 6) body += '<label class="sx__fs">' + ICON_S + '<input type="search" data-sx-fsearch="' + f.key + '" value="' + esc(facetSearch[f.key] || '') + '" placeholder="Search ' + esc(f.label.toLowerCase()) + '" aria-label="Search ' + esc(f.label) + '" autocomplete="off"></label>';
      body += '<div class="sx__cbs" role="group" aria-label="' + esc(f.label) + '">' + shown.map(function (v) {
        var on = sel.indexOf(v) >= 0, cid = 'sx-cb-' + (++fid);
        return '<label class="sx__cb' + (on ? ' is-on' : '') + '" for="' + cid + '"><input type="checkbox" id="' + cid + '" data-sx-attr="' + esc(f.attr) + '" value="' + esc(v) + '"' + (on ? ' checked' : '') + '><span class="sx__box" aria-hidden="true"></span><span class="sx__cbt' + (f.mono ? ' sx__mono' : '') + '">' + esc(v) + '</span></label>';
      }).join('') + (term && !list.length ? '<p class="sx__none">No ' + esc(f.label.toLowerCase()) + ' matches \u201c' + esc(facetSearch[f.key]) + '\u201d</p>' : '') + '</div>';
      if (!term && list.length > 5) body += '<button type="button" class="sx__more" data-sx-more="' + f.key + '" aria-controls="' + id + '-b">' + (expanded[f.key] ? 'Show less' : 'Show more') + '</button>';
      return '<section class="sx__fac' + (isOpenF ? ' is-open' : '') + '" data-sx-fac="' + f.key + '"><h3 class="sx__fh"><button type="button" aria-expanded="' + isOpenF + '" aria-controls="' + id + '-b" data-sx-toggle="' + f.key + '">' +
        esc(f.label) + (sel.length ? ' <span class="sx__fn">(' + sel.length + ')</span>' : '') + (isOpenF ? ICON_UP : ICON_DOWN) + '</button></h3>' +
        '<div class="sx__fb" id="' + id + '-b"' + (isOpenF ? '' : ' hidden') + '>' + body + '</div></section>';
    }).join('');
    facetsEl.innerHTML = html;
  }
  function activeChips() {
    var out = [];
    Object.keys(filters).forEach(function (a) { filters[a].forEach(function (v) { out.push({ attr: a, v: v, label: (BY_ATTR[a] || { label: a }).label }); }); });
    return out;
  }
  function activeChipsHtml() {
    return activeChips().map(function (c) {
      return '<button type="button" class="sx__achip" data-sx-rm-attr="' + esc(c.attr) + '" data-sx-rm-v="' + esc(c.v) + '" aria-label="Remove filter ' + esc(c.label + ': ' + c.v) + '"><span class="sx__k">' + esc(c.label) + ':</span> <span class="sx__av">' + esc(c.v) + '</span><i aria-hidden="true">\u2715</i></button>';
    }).join('');
  }
  function suggestionChips(r) {
    var out = [];
    CHIP_ATTRS.forEach(function (a) {
      if (out.length >= 4 || (filters[a] || []).length) return;
      var f = (r.facets && r.facets[a]) || {};
      var best = Object.keys(f).filter(function (k) { return f[k] > 0 && f[k] < r.nbHits; }).sort(function (x, y) { return f[y] - f[x]; })[0];
      if (best) out.push({ attr: a, v: best, label: BY_ATTR[a].label });
    });
    return out;
  }
  function renderChips(r) {
    var a = activeChipsHtml(), s = r && r.nbHits ? suggestionChips(r) : [];
    var html = a + (a ? '<button type="button" class="sx__clear" data-sx-clearall>Clear all</button>' : '') + (a && s.length ? '<span class="sx__vsep" aria-hidden="true"></span>' : '') +
      s.map(function (c) { return '<button type="button" class="sx__qchip" data-sx-add-attr="' + esc(c.attr) + '" data-sx-add-v="' + esc(c.v) + '" aria-label="Add filter ' + esc(c.label + ': ' + c.v) + '"><span class="sx__k">' + esc(c.label) + '</span> ' + esc(c.v) + '</button>'; }).join('');
    chipsEl.innerHTML = html;
    chipsEl.hidden = !html;
    if (dsel) { dsel.innerHTML = a; dsel.hidden = !a; }
  }
  var optSeq = 0;
  // fix3: first row loads eagerly (first two at high priority), the rest lazily
  var EAGER_N = { grid: 5, list: 6, mobile: 4 };
  function imgAttrs(v, i) {
    var n = EAGER_N[v === 'grid' || v === 'list' ? v : 'mobile'];
    return i < n ? ' loading="eager"' + (i < 2 ? ' fetchpriority="high"' : '') : ' loading="lazy"';
  }
  function cardHtml(h, v, i) {
    var la = imgAttrs(v, i || 0);
    var sp = parseSpec(h.title), price = money(h.variants_min_price != null ? h.variants_min_price : h.price), id = 'sx-o-' + (++optSeq);
    var title = sp ? sp.rest : h.title, label = h.title + (price ? ', ' + price : '');
    var a = '<a id="' + id + '" role="option" aria-selected="false" class="sx__' + (v === 'grid' ? 'card' : v === 'list' ? 'lrow' : 'mrow') + '" href="/products/' + esc(h.handle) + '" aria-label="' + esc(label) + '">';
    if (v === 'grid') return a + '<span class="sx__ph">' + (h.image ? '<img src="' + esc(img(h.image, '200x200')) + '" alt=""' + la + ' decoding="async" width="150" height="150">' : '') + '</span>' +
      '<span class="sx__spec">' + esc(sp ? sp.display : '') + '</span><span class="sx__ttl">' + esc(title) + '</span><span class="sx__price">' + esc(price) + '</span></a>';
    if (v === 'list') return a + '<span class="sx__lth">' + (h.image ? '<img src="' + esc(img(h.image, '100x100')) + '" alt=""' + la + ' decoding="async" width="44" height="44">' : '') + '</span>' +
      '<span class="sx__spec">' + esc(sp ? sp.display : '') + '</span><span class="sx__ttl1">' + esc(title) + '</span><span class="sx__price">' + esc(price) + '</span></a>';
    return a + '<span class="sx__mth">' + (h.image ? '<img src="' + esc(img(h.image, '120x120')) + '" alt=""' + la + ' decoding="async" width="56" height="56">' : '') + '</span>' +
      '<span class="sx__mtx"><span class="sx__spec">' + esc(sp ? sp.display : '') + '</span><span class="sx__ttl">' + esc(title) + '</span></span><span class="sx__price">' + esc(price) + '</span></a>';
  }
  function setResClass(v) { res.className = 'sx__res sx__res--' + v; root.setAttribute('data-view', v); }
  function renderBar(q, r) {
    var total = isTotalApprox(r) ? null : r.nbHits, v = getView(), href = esc(searchUrl(q));
    barEl.innerHTML = '<span class="sx__count">' + (total != null ? '<b>' + fmtInt(total) + '</b> results' : 'Results') + '<span class="sx__forq"> for \u201c' + esc(q) + '\u201d</span></span><span class="sx__sp"></span>' +
      '<button type="button" class="sx__fbtn" data-sx-drawer aria-haspopup="dialog">Filters' + (filterCount() ? ' (' + filterCount() + ')' : '') + '</button>' +
      '<label class="sx__sort"><span class="sx__k">Sort:</span><select aria-label="Sort results"><option value="relevance" selected>Relevance</option></select></label>' +
      '<span class="sx__seg" role="group" aria-label="View"><button type="button" data-sx-view="grid" aria-label="Grid view" aria-pressed="' + (v === 'grid') + '">' + ICON_GRID + '</button><button type="button" data-sx-view="list" aria-label="List view" aria-pressed="' + (v === 'list') + '">' + ICON_LIST + '</button></span>' +
      '<a class="sx__seeall" href="' + href + '" data-sx-all>See all ' + (total != null ? fmtInt(total) + ' ' : '') + 'results <span aria-hidden="true">\u2192</span></a>';
    if (mfoot) mfoot.innerHTML = '<button type="button" class="sx__btn2" data-sx-drawer aria-haspopup="dialog">Filters (' + filterCount() + ')</button><a class="sx__btn1" href="' + href + '" data-sx-all>See all' + (total != null ? ' ' + fmtInt(total) : '') + '</a>';
    if (dShow) dShow.textContent = total != null ? 'Show ' + fmtInt(total) + ' results' : 'Show results';
  }
  function renderResults(q, r) {
    clearTimeout(skelTimer); root.removeAttribute('data-loading');
    lastRes = r; lastQ = q;
    // remember each facet's values from a reply where that facet was not filtered (same text) -> disjunctive lists, no extra query
    if (memoryQ !== q.toLowerCase()) { facetMemory = {}; memoryQ = q.toLowerCase(); }
    FACET_ATTRS.forEach(function (a) { if (!(filters[a] || []).length && r.facets && r.facets[a]) facetMemory[a] = r.facets[a]; });
    if (!r.nbHits) { renderEmpty(q, r); return; }
    root.setAttribute('data-state', 'results');
    msgEl.hidden = true; res.hidden = false; barEl.hidden = false;
    var v = getView(); setResClass(v);
    var hits = boostExactSize(r.hits || [], q);
    res.innerHTML = (v === 'list' ? '<div class="sx__lhead" aria-hidden="true"><span></span><span>Size</span><span>Product</span><span>Price</span></div>' : '') + hits.map(function (h, i) { return cardHtml(h, v, i); }).join('');
    renderBar(q, r); renderChips(r); renderFacets(r);
    navOrder = [].slice.call(res.querySelectorAll('[role=option]')); setActive(-1);
    root.setAttribute('data-counts', isFacetsApprox(r) ? 'approx' : 'exact');
    var total = isTotalApprox(r) ? null : r.nbHits, ac = activeChips();
    setStatus((total != null ? fmtInt(total) + ' results' : 'Results') + ' for ' + q + (ac.length ? ', filtered by ' + ac.map(function (c) { return c.label + ' ' + c.v; }).join(', ') : '') + '. Press down arrow to browse.');
    if (QA) window.EcomSearchFullscreen.last = { q: q, nbHits: r.nbHits, filters: JSON.parse(JSON.stringify(filters)), key: r._sxKey, reqSeq: reqSeq, facetsApprox: isFacetsApprox(r), queries: r._sxQueries || 1, disj: r._disj ? Object.keys(r._disj) : [] };
  }
  function tipsHtml() { return '<div class="sx__tips" role="group" aria-label="Search tips">' + TIPS.map(function (t) { return '<button type="button" class="sx__tip" data-sx-q="' + esc(t[0]) + '"><code>' + esc(t[0]) + '</code>' + esc(t[1]) + '</button>'; }).join('') + '</div>'; }
  function popHtml() { return '<div class="sx__cap">Popular searches</div><div class="sx__pchips" role="group" aria-label="Popular searches">' + POPULAR.map(function (p) { return '<button type="button" data-sx-q="' + esc(p) + '">' + esc(p) + '</button>'; }).join('') + '</div>'; }
  function btnsHtml() { return '<div class="sx__btns"><a class="sx__btn2" href="' + LINKS.quickOrder + '">Quick Order by part #</a><a class="sx__btn2" href="' + LINKS.categories + '">Browse categories</a></div>'; }
  function showMsg(state, html) {
    clearTimeout(skelTimer); root.removeAttribute('data-loading');
    root.setAttribute('data-state', state);
    res.hidden = true; res.innerHTML = ''; barEl.hidden = true; facetsEl.innerHTML = ''; msgEl.hidden = false; msgEl.innerHTML = html;
    navOrder = []; setActive(-1);
    if (mfoot) mfoot.innerHTML = '';
    barEl.innerHTML = ''; if (dShow) dShow.textContent = 'Show results'; // no stale count from the previous query (0 hits / idle / sku)
  }
  function renderIdle() {
    var rec = getRecent();
    renderChips(null);
    showMsg('idle', '<div class="sx__empty"><h2>Search by size, type or part&nbsp;#</h2>' + tipsHtml() +
      (rec.length ? '<div class="sx__cap">Recent searches</div><div class="sx__pchips" role="group" aria-label="Recent searches">' + rec.map(function (p) { return '<button type="button" data-sx-q="' + esc(p) + '">' + esc(p) + '</button>'; }).join('') + '</div>' : '') +
      popHtml() + '</div>');
    setStatus('');
  }
  var altCss = false;
  function ensureAltCss() {
    if (altCss) return; altCss = true;
    var st = document.createElement('style');
    st.textContent = '.sx__alt{margin:0 0 24px;border:1px solid #DDE3EA;border-radius:8px;overflow:hidden}.sx__alth{display:flex;flex-wrap:wrap;gap:8px 12px;align-items:center;justify-content:space-between;padding:12px 16px;background:#F3F5F8}' +
      '.sx__alth h3{margin:0;font-size:15px;font-weight:600}.sx__alth p{margin:2px 0 0!important;font-size:13px!important}.sx__altb{font:inherit;font-size:14px;font-weight:600;padding:8px 12px;border:1px solid currentColor;border-radius:6px;background:#fff;cursor:pointer;color:inherit}' +
      '.sx__alt .sx__mrow:first-child{border-top:0}.sx__alt .sx__mrow{border-top:1px solid #DDE3EA}';
    document.head.appendChild(st);
  }
  function altHtml(a) {
    var act = activeChips(), head, sub = '', btn;
    if (a.kind === 'filters') {
      head = 'Without ' + a.relaxed.map(function (c) { return c.label + ': ' + c.v; }).join(', ');
      var m = a.facets && a.facets['meta.custom.material'];
      if (m) sub = 'Available in ' + Object.keys(m).sort(function (x, y) { return m[y] - m[x]; }).slice(0, 4).join(', ');
      btn = '<button type="button" class="sx__altb" data-sx-clearall>Show all ' + fmtInt(a.nbHits) + '</button>';
    } else {
      head = 'Without \u201c' + a.dropped + '\u201d: \u201c' + a.q + '\u201d' + (act.length ? ' in ' + act.map(function (c) { return c.v; }).join(', ') : '');
      btn = '<button type="button" class="sx__altb" data-sx-q="' + esc(a.q) + '">Show all ' + fmtInt(a.nbHits) + '</button>';
    }
    return '<section class="sx__alt" aria-label="' + esc(head) + '"><div class="sx__alth"><div><h3>' + esc(head) + '</h3>' + (sub ? '<p>' + esc(sub) + '</p>' : '') + '</div>' + btn + '</div>' +
      boostExactSize(a.hits, a.q).map(function (h, i) { return cardHtml(h, 'rows', i); }).join('') + '</section>';
  }
  function renderEmpty(q, r) {
    var f = filterCount();
    if (r && r._alts && r._alts.length) {
      ensureAltCss();
      var act = activeChips(), inTxt = act.length ? ' in ' + act.map(function (c) { return c.v; }).join(', ') : '';
      if (f) { renderChips(null); } else { chipsEl.innerHTML = ''; chipsEl.hidden = true; }
      showMsg('empty', '<div class="sx__empty sx__empty--alt"><h2>No exact match for \u201c' + esc(q) + '\u201d' + esc(inTxt) + '</h2>' +
        '<p>Every word and filter has to match. Close alternatives, with the part we relaxed:</p>' + r._alts.map(altHtml).join('') + '</div>');
      navOrder = [].slice.call(msgEl.querySelectorAll('[role=option]')); setActive(-1);
      setStatus('No exact match for ' + q + inTxt + '. ' + r._alts.length + ' close alternative group' + (r._alts.length > 1 ? 's' : '') + ' shown.');
      if (QA) window.EcomSearchFullscreen.last = { q: q, nbHits: 0, filters: JSON.parse(JSON.stringify(filters)), key: r._sxKey, reqSeq: reqSeq, queries: r._sxQueries || 1, alts: r._alts.map(function (a) { return { kind: a.kind, q: a.q, nbHits: a.nbHits }; }) };
      return;
    }
    if (f) { renderChips(null); } else { chipsEl.innerHTML = ''; chipsEl.hidden = true; }
    showMsg('empty', '<div class="sx__empty"><h2>No matches for \u201c' + esc(q) + '\u201d' + (f ? ' with these filters' : '') + '</h2>' +
      (f ? '<p>Remove a filter or <button type="button" class="sx__clear" data-sx-clearall>clear all filters</button>.</p>' : '<p>Search works best with a size, a type, or a part number. Filters stay hidden until there are results to narrow.</p>') +
      tipsHtml() + popHtml() + btnsHtml() + '</div>');
    setStatus('No matches for ' + q + (f ? ' with the current filters' : '') + '.');
  }
  function renderSku(q, url) {
    renderChips(null);
    showMsg('sku', '<div class="sx__empty"><div class="sx__cap">Exact part number</div><a class="sx__sku" id="sx-sku" href="' + esc(url) + '"><code>' + esc(q) + '</code> Exact part number match: view product <span aria-hidden="true">\u21b5</span></a></div>');
    navOrder = [$('sx-sku')].filter(Boolean); setActive(-1);
    setStatus('Exact part number match for ' + q + '. Press Enter to open it.');
  }
  function showLoading() {
    root.setAttribute('data-loading', 'dim');
    clearTimeout(skelTimer);
    skelTimer = setTimeout(function () {
      root.setAttribute('data-loading', 'skel');
      var v = getView(); setResClass(v);
      var n = res.querySelectorAll('[role=option]').length || (v === 'grid' ? 10 : 8);
      var one = v === 'grid' ? '<div class="sx__card sx__sk" aria-hidden="true"><span class="sx__ph"></span><span class="sx__b w40"></span><span class="sx__b"></span><span class="sx__b w30"></span></div>'
        : '<div class="sx__' + (v === 'list' ? 'lrow' : 'mrow') + ' sx__sk" aria-hidden="true"><span class="sx__b sx__bimg"></span><span class="sx__b w40"></span><span class="sx__b"></span><span class="sx__b w30"></span></div>';
      res.hidden = false; msgEl.hidden = true; barEl.hidden = false;
      res.innerHTML = new Array(Math.min(n, 20) + 1).join(one);
      navOrder = []; setActive(-1);
    }, 300);
  }

  // ---- keyboard / active option ----
  function setActive(i) {
    if (activeIdx >= 0 && navOrder[activeIdx]) { navOrder[activeIdx].classList.remove('is-active'); navOrder[activeIdx].setAttribute('aria-selected', 'false'); }
    activeIdx = i;
    var el = navOrder[i];
    if (el) {
      el.classList.add('is-active'); el.setAttribute('aria-selected', 'true'); input.setAttribute('aria-activedescendant', el.id);
      var sc = mainEl, er = el.getBoundingClientRect(), cr = sc.getBoundingClientRect();
      var bottom = cr.bottom - (mfoot && mqMobile.matches ? 76 : 0);
      if (er.top < cr.top + 60) sc.scrollTop -= (cr.top + 60 - er.top);
      else if (er.bottom > bottom) sc.scrollTop += (er.bottom - bottom) + 8;
    } else input.removeAttribute('aria-activedescendant');
  }
  function columns() {
    if (getView() !== 'grid' || !navOrder.length) return 1;
    var top = navOrder[0].offsetTop, n = 0;
    for (var i = 0; i < navOrder.length && navOrder[i].offsetTop === top; i++) n++;
    return Math.max(1, n);
  }
  function move(dRow, dCol) {
    var n = navOrder.length; if (!n) return false;
    var cols = columns(), i;
    if (activeIdx < 0) { if (dRow > 0 || dCol > 0) i = 0; else return false; }
    else if (dCol) { i = activeIdx + dCol; if (i < 0 || i >= n) return true; }
    else { i = activeIdx + dRow * cols; if (i >= n) return true; if (i < 0) i = -1; }
    setActive(i); return true;
  }

  // ---- open / close / layout ----
  var savedScroll = 0, inerted = [];
  function setInert(on) {
    inerted.forEach(function (el) { el.inert = false; el.removeAttribute('aria-hidden'); }); inerted = [];
    if (!on) return;
    // everything except the path body > ... > wrapper (the dialog = wrapper: form + overlay)
    var node = wrapper;
    while (node && node !== document.body) {
      var p = node.parentElement; if (!p) break;
      [].forEach.call(p.children, function (c) { if (c !== node && !/^(SCRIPT|STYLE|LINK)$/.test(c.tagName)) { if (!c.inert) { c.inert = true; inerted.push(c); } } });
      node = p;
    }
    if (kbd && !kbd.inert) { kbd.inert = true; inerted.push(kbd); }
  }
  function layout() {
    if (!isOpen || !slot || !form) return;
    var vv = window.visualViewport, h = vv ? vv.height : window.innerHeight, top = vv ? vv.offsetTop : 0;
    docEl.style.setProperty('--sx-vvh', Math.round(h) + 'px');
    docEl.style.setProperty('--sx-vvtop', Math.round(top) + 'px');
    var s = slot.getBoundingClientRect();
    form.style.top = Math.round(s.top) + 'px'; form.style.left = Math.round(s.left) + 'px'; form.style.width = Math.round(s.width) + 'px'; form.style.height = Math.round(s.height) + 'px';
  }
  function open(fromPop) {
    if (isOpen) { layout(); return; }
    isOpen = true;
    savedScroll = window.scrollY;
    root.hidden = false;
    wrapper.setAttribute('role', 'dialog'); wrapper.setAttribute('aria-modal', 'true'); wrapper.setAttribute('aria-label', 'Search');
    docEl.classList.add('sx-open');
    if (headerSection) headerSection.classList.add('sx-raise');
    input.setAttribute('aria-expanded', 'true');
    setInert(true);
    if (!fromPop) { try { if (!(history.state && history.state.sx)) history.pushState({ sx: 1 }, '', location.href); } catch (e) {} }
    layout();
    var q = input.value.trim();
    if (q.length < MIN_CHARS) renderIdle(); else if (!lastRes || lastQ !== q) onType(true);
  }
  function close(fromPop) {
    if (!isOpen) return;
    if (drawerOpen) setDrawer(false, true);
    isOpen = false;
    root.hidden = true;
    ['role', 'aria-modal', 'aria-label'].forEach(function (a) { wrapper.removeAttribute(a); });
    docEl.classList.remove('sx-open');
    if (headerSection) headerSection.classList.remove('sx-raise');
    form.style.top = form.style.left = form.style.width = form.style.height = '';
    input.setAttribute('aria-expanded', 'false');
    setActive(-1); setInert(false);
    window.scrollTo(0, savedScroll);
    // X / Esc: pop our own entry so no extra history entry is left. Back (popstate) / navigation: never call back().
    if (!fromPop) { try { if (history.state && history.state.sx) { ignorePop = true; history.back(); } } catch (e) {} }
    suppressFocusOpen = true; input.focus(); setTimeout(function () { suppressFocusOpen = false; }, 0);
  }
  var ignorePop = false;
  window.addEventListener('popstate', function (e) {
    if (ignorePop) { ignorePop = false; return; }                 // the back() we made ourselves on X / Esc
    var sx = e.state && e.state.sx;
    if (isOpen && !sx) close(true);                                  // Back while open: close, stay on the page
    else if (!isOpen && sx) { open(true); input.focus(); }           // Forward onto our entry: reopen
  });
  // returning via bfcache after navigating away: never show a stale open dialog
  window.addEventListener('pageshow', function (e) { if (e.persisted && isOpen && !(history.state && history.state.sx)) close(true); });
  // leaving to a product or /search from the dialog REPLACES our {sx:1} entry, so Back from the new page returns to the
  // page itself (dialog closed) and no stray entry is left behind
  function leave(url) {
    saveRecent(input.value);
    if (history.state && history.state.sx) location.replace(url); else location.assign(url);
  }

  var lastDrawerTrigger = null;
  function setDrawer(on, silent) {
    drawerOpen = on;
    root.classList.toggle('is-drawer', on);
    if (on) { side.setAttribute('role', 'dialog'); side.setAttribute('aria-modal', 'true'); side.setAttribute('aria-label', 'Filters'); dim.hidden = false; lastDrawerTrigger = document.activeElement; setTimeout(function () { (dClose || side).focus(); }, 0); }
    else { side.removeAttribute('role'); side.removeAttribute('aria-modal'); side.setAttribute('aria-label', 'Filters'); dim.hidden = true; if (!silent) (lastDrawerTrigger && document.contains(lastDrawerTrigger) ? lastDrawerTrigger : input).focus(); }
  }

  // ---- typing / searching ----
  function search(immediate) {
    var q = input.value.trim(), seq = ++typedSeq;
    if (clearBtn) clearBtn.hidden = !input.value;
    clearTimeout(debounceTimer);
    if (q.length < MIN_CHARS) { ++reqSeq; lastRes = null; renderIdle(); return; }
    var key = q.toLowerCase() + '#' + filtersKey();
    if (cache[key]) { renderResults(q, cache[key]); return; }
    showLoading();
    debounceTimer = setTimeout(function () {
      var jump = window.EcomSkuJump;
      var check = (jump && jump.looksLikeSku(q) && !filterCount()) ? jump.resolve(q) : Promise.resolve(null);
      check.then(function (url) {
        if (seq !== typedSeq) return;
        if (url) { renderSku(q, url); return; }
        return runSearch(q).then(function (r) {
          if (seq !== typedSeq || r === STALE) return;                                 // superseded
          if (r && r._sxKey !== input.value.trim().toLowerCase() + '#' + filtersKey()) return; // not for the current text + filters
          if (r === null) { root.removeAttribute('data-loading'); clearTimeout(skelTimer); return; }
          renderResults(q, r);
        });
      }).catch(function (e) { root.removeAttribute('data-loading'); clearTimeout(skelTimer); if (window.console) console.warn('[ecom-search] query failed', e); });
    }, immediate ? 0 : DEBOUNCE_MS);
  }
  function onType(immediate) { if (!isOpen) { open(); if (!immediate) return search(false); return; } search(immediate); }
  function toggleFilter(attr, v, on) {
    var a = (filters[attr] || []).slice(), i = a.indexOf(v);
    if (on && i < 0) a.push(v); if (!on && i >= 0) a.splice(i, 1);
    if (a.length) filters[attr] = a; else delete filters[attr];
    search(true);
  }

  input.addEventListener('input', function () { onType(false); });
  input.addEventListener('focus', function () { getClient(); if (!suppressFocusOpen && !isOpen) open(); });
  input.addEventListener('click', function () { if (!isOpen) open(); });
  if (wrapper) wrapper.addEventListener('pointerenter', function () { getClient(); }, { once: true });

  input.addEventListener('keydown', function (e) {
    if (e.isComposing || !isOpen) return;
    var k = e.key, hasActive = activeIdx >= 0;
    if (k === 'ArrowDown') { if (move(1, 0)) e.preventDefault(); }
    else if (k === 'ArrowUp') { if (hasActive) { e.preventDefault(); move(-1, 0); } }
    else if ((k === 'ArrowRight' || k === 'ArrowLeft') && hasActive && getView() === 'grid') { e.preventDefault(); move(0, k === 'ArrowRight' ? 1 : -1); }
    else if (k === 'Enter') {
      e.preventDefault();
      if (hasActive && navOrder[activeIdx]) leave(navOrder[activeIdx].getAttribute('href'));
      else if (root.getAttribute('data-state') === 'sku' && $('sx-sku')) leave($('sx-sku').getAttribute('href'));
      else if (input.value.trim()) leave(searchUrl(input.value.trim()));
    }
  });
  if (form) form.addEventListener('submit', function (e) { if (isOpen && input.value.trim()) { e.preventDefault(); leave(searchUrl(input.value.trim())); } });
  if (clearBtn) { clearBtn.addEventListener('mousedown', function (e) { e.preventDefault(); }); clearBtn.addEventListener('click', function () { input.value = ''; input.focus(); search(true); }); }
  if (closeBtn) closeBtn.addEventListener('click', function () { close(); });

  // Esc + focus trap (Tab cycles inside the dialog, or inside the drawer when open)
  function focusables(scope) {
    return [].filter.call(scope.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]):not([type=hidden]),select,[tabindex]:not([tabindex="-1"])'), function (el) {
      return !el.closest('[hidden]') && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden' && el.tabIndex !== -1;
    });
  }
  document.addEventListener('keydown', function (e) {
    if (!isOpen) return;
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); if (drawerOpen) setDrawer(false); else close(); return; }
    if (e.key !== 'Tab') return;
    var list = drawerOpen ? focusables(side) : focusables(wrapper);
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1], a = document.activeElement;
    if (e.shiftKey && (a === first || list.indexOf(a) < 0)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && (a === last || list.indexOf(a) < 0)) { e.preventDefault(); first.focus(); }
  }, true);

  // ---- delegated clicks ----
  root.addEventListener('load', function (e) { var t = e.target; if (t && t.tagName === 'IMG' && t.parentNode) t.parentNode.classList.add('sx-ld'); }, true); // fix3
  root.addEventListener('click', function (e) {
    var t = e.target, el;
    if ((el = t.closest('[data-sx-q]'))) { input.value = el.getAttribute('data-sx-q'); input.focus(); search(true); return; }
    if ((el = t.closest('[data-sx-rm-attr]'))) { toggleFilter(el.getAttribute('data-sx-rm-attr'), el.getAttribute('data-sx-rm-v'), false); (drawerOpen ? dClose : input).focus(); return; }
    if ((el = t.closest('[data-sx-add-attr]'))) { toggleFilter(el.getAttribute('data-sx-add-attr'), el.getAttribute('data-sx-add-v'), true); input.focus(); return; }
    if ((el = t.closest('[data-sx-clearall]'))) { filters = {}; search(true); (drawerOpen ? dClose : input).focus(); return; }
    if ((el = t.closest('[data-sx-toggle]'))) {
      var k = el.getAttribute('data-sx-toggle'); openFacets[k] = !openFacets[k]; sstore(OPEN_KEY, openFacets);
      if (lastRes) { renderFacets(lastRes); var b = facetsEl.querySelector('[data-sx-toggle="' + k + '"]'); if (b) b.focus(); } return;
    }
    if ((el = t.closest('[data-sx-more]'))) { var m = el.getAttribute('data-sx-more'); expanded[m] = !expanded[m]; if (lastRes) { renderFacets(lastRes); var mb = facetsEl.querySelector('[data-sx-more="' + m + '"]'); if (mb) mb.focus(); } return; }
    if ((el = t.closest('[data-sx-view]'))) { view = el.getAttribute('data-sx-view'); store(VIEW_KEY, view); if (lastRes) renderResults(lastQ, lastRes); var vb = barEl.querySelector('[data-sx-view="' + view + '"]'); if (vb) vb.focus(); return; }
    if ((el = t.closest('[data-sx-drawer]'))) { setDrawer(true); return; }
    if (t.closest('#sx-dclose') || t.closest('#sx-dshow') || t === dim) { setDrawer(false); return; }
    var link = t.closest('a[href]');
    if (link && !(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button > 0) && link.target !== '_blank') { e.preventDefault(); leave(link.getAttribute('href')); return; }
    if (link) saveRecent(input.value); // new tab: let the browser handle it
  });
  root.addEventListener('change', function (e) {
    var cb = e.target.closest('input[type=checkbox][data-sx-attr]'); if (!cb) return;
    var attr = cb.getAttribute('data-sx-attr'), v = cb.value, sec = cb.closest('[data-sx-fac]'), key = sec && sec.getAttribute('data-sx-fac');
    toggleFilter(attr, v, cb.checked);
    // keep keyboard focus on the same checkbox after the facets re-render
    var refocus = function () { var n = facetsEl.querySelector('[data-sx-fac="' + key + '"] input[type=checkbox][value="' + (window.CSS && CSS.escape ? CSS.escape(v) : v) + '"]'); if (n) n.focus(); };
    setTimeout(refocus, 0); var iv = setInterval(function () { if (!root.getAttribute('data-loading')) { clearInterval(iv); refocus(); } }, 60); setTimeout(function () { clearInterval(iv); }, 4000);
  });
  root.addEventListener('input', function (e) {
    var fs = e.target.closest('[data-sx-fsearch]'); if (!fs) return;
    var k = fs.getAttribute('data-sx-fsearch'); facetSearch[k] = fs.value;
    var pos = fs.selectionStart;
    if (lastRes) renderFacets(lastRes);           // client-side only: no Algolia call
    var n = facetsEl.querySelector('[data-sx-fsearch="' + k + '"]'); if (n) { n.focus(); try { n.setSelectionRange(pos, pos); } catch (er) {} }
  });
  root.addEventListener('pointermove', function (e) {
    if (e.pointerType && e.pointerType !== 'mouse') return;
    var el = e.target.closest('[role=option]'); if (!el) return;
    var i = navOrder.indexOf(el); if (i >= 0 && i !== activeIdx) setActive(i);
  });
  root.addEventListener('mousedown', function (e) { if (e.target.closest('[role=option]')) e.preventDefault(); });

  var lastMode = null;
  window.addEventListener('resize', function () {
    layout();
    var mode = (mqMobile.matches ? 'm' : mqDrawer.matches ? 't' : 'd') + getView();
    if (drawerOpen && !mqDrawer.matches) setDrawer(false, true);
    if (isOpen && lastRes && lastRes.nbHits && mode !== lastMode) renderResults(lastQ, lastRes);
    lastMode = mode;
  });
  if (window.visualViewport) { window.visualViewport.addEventListener('resize', layout); window.visualViewport.addEventListener('scroll', layout); }

  window.EcomSearchFullscreen = { open: open, close: close, qa: QA, version: 'strict-2026-10-19' };
  window.EcomSearchDropdown = window.EcomSearchFullscreen; // the "/" shortcut and other callers keep working
})();
